import { type CompiledDocument, initialize, PageRange, Template } from '@oicana/browser';
import {
    DocumentsWorkerRequest,
    DocumentsWorkerRequestKind,
    DocumentsWorkerResponse,
    DocumentsWorkerResponseKind,
} from './types';

const templateCache: Map<string, Promise<Template>> = new Map();
let initState: 'idle' | 'initializing' | 'initialized' | 'error' = 'idle';
let initPromise: Promise<void> = new Promise(() => {});
let initError: Error;

export const compiledDocuments: Map<number, CompiledDocument> = new Map();
let documentTokenCounter = 0;
// Tracks which port compiled which documents, so a Disconnect (sent when that tab goes away for
// good) can release everything it left behind even if it never got to send individual Release
// messages. The Provider only ever sends Disconnect for a real unload (see the `event.persisted`
// check around its `pagehide` handler) — a page merely entering the bfcache never triggers it —
// so a missing entry here reliably means the port is gone for good, which the Compile handler
// relies on to dispose documents that finish compiling after their tab has already closed.
export const portDocuments: Map<MessagePort, Set<number>> = new Map();

const initializeWasm = async () => {
    switch (initState) {
        case 'idle':
            try {
                initState = 'initializing';
                initPromise = initialize();
                await initPromise;
                initState = 'initialized';
            } catch (e) {
                initError = e as Error;
                console.error(e);
                initState = 'error';
            }
            break;
        case 'initializing':
            return initPromise;
        case 'initialized':
            return Promise.resolve();
        case 'error':
            return Promise.reject(initError);
    }
};

const postMessage = (port: MessagePort, message: DocumentsWorkerResponse) => {
    switch (message.kind) {
        case DocumentsWorkerResponseKind.Page: {
            const copy = new Uint8Array(message.preview);
            port.postMessage({ ...message, preview: copy.buffer }, [copy.buffer]);
            break;
        }
        default:
            port.postMessage(message);
    }
};

const disposeDocument = (documentToken: number) => {
    compiledDocuments.get(documentToken)?.dispose();
    compiledDocuments.delete(documentToken);
};

const postError = (port: MessagePort, nonce: number, e: unknown) => {
    const message = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
    console.error(e);
    postMessage(port, { kind: DocumentsWorkerResponseKind.Error, nonce, message });
};

const isReady = async () => {
    try {
        await initializeWasm();
        return true;
    } catch (error) {
        console.error(error);
        return false;
    }
};

const getTemplate = (documentsApi: string, templateId: string, authToken: string): Promise<Template> => {
    if (templateId === undefined) {
        throw Error('template id was not defined');
    }
    const template = templateCache.get(templateId);
    if (template === undefined) {
        const prepareTemplate = async () => {
            const files = await loadTemplate(documentsApi, templateId, authToken);
            return new Template(files);
        };
        const loadingTemplate = prepareTemplate();
        templateCache.set(templateId, loadingTemplate);
        return loadingTemplate;
    }
    return template;
};

// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
const loadTemplate = async (documentsApi: string, templateId: string, authToken: string) => {
    const response = await fetch(`${documentsApi}/templates/${templateId}`, {
        headers: {
            Authorization: `Bearer ${authToken}`,
        },
        credentials: 'include',
    });
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
};

// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
export const handleRelease = (port: MessagePort, documentToken: number) => {
    disposeDocument(documentToken);
    portDocuments.get(port)?.delete(documentToken);
};

// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
export const handleDisconnect = (port: MessagePort) => {
    for (const documentToken of portDocuments.get(port) ?? []) {
        disposeDocument(documentToken);
    }
    portDocuments.delete(port);
};

// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
export const handleCompile = async (
    port: MessagePort,
    data: Extract<DocumentsWorkerRequest, { kind: DocumentsWorkerRequestKind.Compile }>,
) => {
    const { nonce } = data;
    try {
        const { template: templateId, authToken, jsonDatasets, blobDatasets, documentsApi } = data;
        const template = await getTemplate(documentsApi, templateId, authToken);
        const doc = template.compile(jsonDatasets, blobDatasets);

        // The port may have disconnected while the template fetch/compile above was in flight
        // (e.g. the tab was closed). Its entry is then gone from portDocuments, and no one is
        // left to ever Release this document, so dispose it immediately instead of leaking it.
        const ownedDocuments = portDocuments.get(port);
        if (ownedDocuments === undefined) {
            doc.dispose();
            return;
        }

        const documentToken = ++documentTokenCounter;
        compiledDocuments.set(documentToken, doc);
        ownedDocuments.add(documentToken);
        postMessage(port, {
            kind: DocumentsWorkerResponseKind.Compiled,
            nonce,
            documentToken,
            pages: [...doc.pages],
        });
    } catch (e) {
        postError(port, nonce, e);
    }
};

// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
export const handleRenderPage = (
    port: MessagePort,
    data: Extract<DocumentsWorkerRequest, { kind: DocumentsWorkerRequestKind.RenderPage }>,
) => {
    const { nonce } = data;
    try {
        const { documentToken, pageIndex, pixelsPerPt } = data;
        const doc = compiledDocuments.get(documentToken);
        if (!doc) {
            throw new Error(`Document with token ${documentToken} is not compiled (anymore)`);
        }
        const previewData = doc.exportPng(pixelsPerPt, PageRange.single(pageIndex));
        const previewBuffer = previewData.buffer as ArrayBuffer;
        postMessage(port, { kind: DocumentsWorkerResponseKind.Page, preview: previewBuffer, nonce });
    } catch (e) {
        postError(port, nonce, e);
    }
};

// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
export const handlePortMessage = async (port: MessagePort, data: DocumentsWorkerRequest) => {
    if (data.kind === DocumentsWorkerRequestKind.Release) {
        handleRelease(port, data.documentToken);
        return;
    }

    if (data.kind === DocumentsWorkerRequestKind.Disconnect) {
        handleDisconnect(port);
        return;
    }

    if (!(await isReady())) {
        const message = `Documents worker initialization failed: ${initError?.message ?? 'unknown error'}`;
        console.error(message);
        postMessage(port, { kind: DocumentsWorkerResponseKind.Error, nonce: data.nonce, message });
        return;
    }

    switch (data.kind) {
        case DocumentsWorkerRequestKind.Compile:
            await handleCompile(port, data);
            break;
        case DocumentsWorkerRequestKind.RenderPage:
            handleRenderPage(port, data);
            break;
    }
};

// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
addEventListener('connect', async (event: Event) => {
    const messageEvent = event as MessageEvent;
    const port = messageEvent.ports[0];

    portDocuments.set(port, new Set());

    port.onmessage = (event: MessageEvent<DocumentsWorkerRequest>) => handlePortMessage(port, event.data);
    port.start();

    if (await isReady()) {
        postMessage(port, { kind: DocumentsWorkerResponseKind.Ready });
    } else {
        postMessage(port, { kind: DocumentsWorkerResponseKind.Broken });
    }
});
